import { setupApi } from '@/api/config-setup'
import { DatabaseAlerIcon } from '@/components/icons'
import useDiscoverDataDecoder from '@/features/design-system/hooks/setup/useDiscoverData'
import { CONFIG_SETUP_ROUTES } from '@/features/design-system/routes'
import { NotifyService } from '@/helpers'
import { Box, Button, Stack, Typography } from '@mui/material'
import { useEffect, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import SetUpHeader from '../../../common/SetUpHeader'
import SetUpLayout from '../../../common/SetUpLayout'
import CircularStatic from '../../SetUpThird/SetUpThird/CircularStatic'
import ReviewControllers from './components/ReviewControllers'
import classes from './styles.module.scss'
import useSearchInterface from '../../../../../admin/hooks/interface/useSearchInterface'
import useValidateCurrentInterface from '@features/design-system/hooks/setup/useValidateCurrentInterface'
import { OPERATIONS_ROUTES } from '@/constant/route'

type Props = {}

const DiscoveryData = (props: Props) => {
  const navigate = useNavigate()
  // SCRUM-976: pin to URL :id, not the global selected interface (AuthContext/localStorage).
  const { id } = useParams()
  const interfaceId = Number(id)

  const [isError, setIsError] = useState<boolean>(false)
  const [isOpenReviewController, setIsOpenReviewController] =
    useState<boolean>(false)
  const [progress, setProgress] = useState(0)

  const { data: allInterfaces } = useSearchInterface()

  const { isValidInterface, selectedInterface, isLoading } = useValidateCurrentInterface({
    currentInterfaceId: id,
    allInterfaces,
    redirectTo: OPERATIONS_ROUTES.index.to,
    autoRedirect: false,
  })


  const fetchData = async () => {
    setIsError(false)
    await setupApi
      .getDiscoverData(Number(interfaceId))
      .then((res) => {
        setProgress(100)
        setTimeout(() => {
          setIsOpenReviewController(true)
        }, 2000)
      })
      .catch((e) => {
        NotifyService.error(e)
        setIsError(true)
        setProgress(0)
      })
  }

  useEffect(() => {
    fetchData()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [interfaceId])

  const onBack = () => {
    navigate(CONFIG_SETUP_ROUTES.setupFirst.to(id || ''))
  }

  const progressRef = useRef(progress)

  useEffect(() => {
    const intervalId = setInterval(() => {
      if (progressRef.current < 90) {
        setProgress((prevProgress) => {
          const newProgress = prevProgress + 10
          progressRef.current = newProgress > 100 ? 100 : newProgress
          return progressRef.current
        })
      } else {
        clearInterval(intervalId)
      }
    }, 5000)

    return () => clearInterval(intervalId)
  }, [])

  const { mutate: discoverData, isLoading: isResyncing } =
    useDiscoverDataDecoder()

  const onResync = () => {
    discoverData({
      interfaceId: interfaceId,
    })
  }

  return (
    <SetUpLayout
      title="Discover Data"
      header={<SetUpHeader hideSelect={true} />}
    >
      <Box className={classes.root}>
        <Typography color="text.main" className={classes.pageTitle}>
          Discover Data
        </Typography>
        {!isError ? (
          <>
            <Stack>
              <CircularStatic progress={progress} />
            </Stack>
            <Typography color="primary.main" className={classes.title}>
              Discovering Data
            </Typography>
            <Typography className={classes.description}>
              Please do not close the window
            </Typography>
            <Box className={classes.actions}>
              <Button className={classes.btnCanel} onClick={onBack}>
                Cancel
              </Button>
            </Box>
          </>
        ) : (
          <>
            <DatabaseAlerIcon className={classes.errorIcon} />
            <Typography className={classes.title}>
              Unable to discover data
            </Typography>
            <Typography className={classes.description}>
              Try again or go back to previous page
            </Typography>
            <Box className={classes.actions}>
              <Button
                color="secondary"
                className={classes.btnCanel}
                onClick={onBack}
              >
                Back
              </Button>
              <Button
                variant="primary"
                className={classes.btnTry}
                onClick={fetchData}
              >
                Try Again
              </Button>
            </Box>
          </>
        )}
      </Box>
      {isOpenReviewController && (
        <ReviewControllers
          isResyncing={isResyncing}
          open={isOpenReviewController}
          onResync={onResync}
        />
      )}
    </SetUpLayout>
  )
}

export default DiscoveryData
